import { MESSAGE_TYPE } from 'shared/constants/messages';
import { isValidEmail as isContactEmail } from 'shared/helpers/Validators';

export const parseEmailList = value => {
  if (!value || !String(value).trim()) return [];

  return String(value)
    .split(/[,;\n]+/)
    .map(email => email.trim())
    .filter(Boolean);
};

export const isValidEmail = email => {
  if (!email || typeof email !== 'string' || !email.trim()) return false;
  return isContactEmail(email);
};

export const validEmailsByComma = value => {
  if (!value || !value.length) return true;
  const emails = parseEmailList(value);
  return emails.every(email => isValidEmail(email));
};

export const normalizeEmailList = list => {
  const values = !list ? [] : Array.isArray(list) ? list : [list];
  return [
    ...new Set(
      values
        .map(email => String(email || '').trim().toLowerCase())
        .filter(Boolean)
    ),
  ];
};

/**
 * Merge email.* headers with top-level to_emails/cc_emails so Reply all
 * matches what MailHead shows (which falls back across both sources).
 */
export const mergeEmailAttributesForReply = (contentAttributes = {}) => {
  const email = { ...(contentAttributes.email || {}) };

  return {
    ...email,
    from: normalizeEmailList(email.from),
    to: normalizeEmailList([
      ...normalizeEmailList(email.to),
      ...normalizeEmailList(contentAttributes.to_emails),
    ]),
    cc: normalizeEmailList([
      ...normalizeEmailList(email.cc),
      ...normalizeEmailList(contentAttributes.cc_emails),
    ]),
    bcc: normalizeEmailList([
      ...normalizeEmailList(email.bcc),
      ...normalizeEmailList(contentAttributes.bcc_emails),
    ]),
  };
};

/**
 * True when we should Reply-all to the sender (From), like Outlook/Gmail on a
 * received mail. Uses From vs inbox, not only message_type — INTERNAL/synced
 * mails can be stored as outgoing while From is still another party.
 */
export const shouldReplyToSender = ({
  emailAttributes = {},
  inboxEmails = [],
  messageType,
} = {}) => {
  const inboxSet = new Set(normalizeEmailList(inboxEmails));
  const from = normalizeEmailList(emailAttributes.from);
  const fromIsOtherParty =
    from.length > 0 && from.some(email => !inboxSet.has(email));

  if (fromIsOtherParty) return true;
  return messageType === MESSAGE_TYPE.INCOMING;
};

/**
 * Build To/CC like Outlook/Gmail "Reply all" for a given email message.
 * BCC is never included (same as Outlook/Gmail).
 *
 * CW-1511: when a third party sent the mail and the conversation contact is
 * not already in From/To/Cc, add the contact to CC so they stay on the thread.
 */
export const buildReplyAllRecipients = ({
  emailAttributes = {},
  inboxEmails = [],
  isIncoming = true,
  conversationContactEmail = '',
} = {}) => {
  const inboxSet = new Set(normalizeEmailList(inboxEmails));
  const excludeInbox = emails => emails.filter(email => !inboxSet.has(email));

  const from = normalizeEmailList(emailAttributes.from);
  const to = normalizeEmailList(emailAttributes.to);
  const cc = normalizeEmailList(emailAttributes.cc);

  let resultTo = [];
  let resultCc = [];

  if (isIncoming) {
    // Reply all to a received mail: To = sender(s), CC = other To/CC participants
    resultTo = excludeInbox(from);
    const toSet = new Set(resultTo);
    resultCc = excludeInbox([...to, ...cc]).filter(email => !toSet.has(email));

    const contact = normalizeEmailList(conversationContactEmail)[0];
    if (contact && !inboxSet.has(contact)) {
      const alreadyIncluded =
        toSet.has(contact) ||
        resultCc.includes(contact) ||
        from.includes(contact);
      if (!alreadyIncluded) {
        resultCc.push(contact);
      }
    }
  } else {
    // Reply all to a sent mail: reuse original To/CC (minus inbox)
    resultTo = excludeInbox(to);
    const toSet = new Set(resultTo);
    resultCc = excludeInbox(cc).filter(email => !toSet.has(email));
  }

  return {
    toEmails: resultTo.join(', '),
    ccEmails: resultCc.join(', '),
    bccEmails: '',
  };
};
